<template>
  <YscModal
    v-show="show"
    no-padding
    fullscreen
    is-header-left-on-mobile
    @close="onClose"
  >
    <template #title>
      {{ currentFormInfo.title }}
    </template>

    <YscForm
      v-show="isMainFormActive"
      :id="FORM_MAP.MAIN"
      class="!space-y-0"
      @submit="onSubmitFilter(formMain)"
    >
      <AppSearchFiltersInstantBookingOption
        v-if="!isCrossedResults"
        v-model="formMain.instant_booking"
        class="border-gray-200 border-t"
      />

      <div class="flex flex-col items-start py-6 px-4 border-t border-gray-200 gap-4">
        <label
          class="font-semibold"
          for="max_price"
        >
          {{ $t('components.app_search.filters.price_per_day_max') }}
        </label>
        <div class="w-full flex flex-row gap-9 items-center">
          <YscInputSlider
            id="max_price"
            v-model="coercedMaxPrice"
            name="max_price"
            :min="PRICE_RANGE.MIN"
            :max="PRICE_RANGE.MAX"
            :step="5"
            class="grow"
          />
          <span
            class="text-sm font-semibold w-16 text-center rounded px-3 py-2 border-[3px] border-black"
          >{{ $n(coercedMaxPrice, { ...currency, maximumFractionDigits: 0 }) }}</span>
        </div>
      </div>

      <AppSearchFilterFormSection
        :title="$t('commons.travelers')"
      >
        <div class="flex justify-between w-full items-center">
          <label>{{ showAdvancedPassengers ? $t('components.app_search.filters.seats') : $t('components.app_search.filters.travelers') }}</label>
          <YscInputNumber
            id="seatsbelts"
            v-model="formMain.seatbelts"
            class="text-lg"
            :min="0"
            :max="6"
          >
            {{ formatTravelersCount(formMain.seatbelts) }}
          </YscInputNumber>
        </div>
        <div
          v-if="showAdvancedPassengers"
          class="flex justify-between w-full items-center"
        >
          <label>{{ $t('components.app_search.filters.beds') }}</label>
          <YscInputNumber
            id="beds"
            v-model="formMain.beds"
            class="text-lg"
            :min="0"
            :max="6"
          >
            {{ formatTravelersCount(formMain.beds) }}
          </YscInputNumber>
        </div>
        <button
          class="link link-primary text-sm"
          role="button"
          type="button"
          @click="showAdvancedPassengers = !showAdvancedPassengers"
        >
          {{ showAdvancedPassengers ? $t('components.app_search.display_less_criterias') : $t('components.app_search.display_all_criterias') }}
        </button>
      </AppSearchFilterFormSection>

      <AppSearchFilterFormSection
        :title="$t('components.app_search.filters.preferences')"
      >
        <YscInputCheckbox
          id="km_unlimited"
          v-model="formMain.km_unlimited"
          name="km_unlimited"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsMileage
              class="h-5 w-5"
              aria-hidden="true"
            />
            {{ $t('components.app_search.filters.km_unlimited') }}
          </div>
        </YscInputCheckbox>
        <YscInputCheckbox
          id="international"
          v-model="formMain.international"
          name="international"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsInternational
              aria-hidden="true"
              class="h-5 w-5"
            />
            {{ $t('components.app_search.filters.international') }}
          </div>
        </YscInputCheckbox>
      </AppSearchFilterFormSection>
      <AppSearchFilterFormSection :title="$t('components.app_search.filters.discounts')">
        <YscInputCheckbox
          v-if="!isCrossedResults"
          id="discount_early"
          v-model="formPrices.discount_early"
          name="discount_early"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsDiscount
              aria-hidden="true"
              class="h-5 w-5"
            />
            {{ $t('components.app_price_form_early.title') }}
          </div>
        </YscInputCheckbox>
        <YscInputCheckbox
          id="discount_weekly"
          v-model="formPrices.discount_weekly"
          name="discount_weekly"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsCoupon
              aria-hidden="true"
              class="h-5 w-5"
            />
            {{ $t('components.app_price_form_discount.title') }}
          </div>
        </YscInputCheckbox>
      </AppSearchFilterFormSection>

      <div class="flex flex-col items-start py-6 px-4 border-t border-gray-200 gap-4">
        <h5 class="text-base font-semibold">
          {{ $t('components.app_search.filters.vehicle_type') }}
        </h5>
        <div class="flex flex-row gap-2 w-full">
          <YscInputCheckboxCard
            v-for="option in mainFormVehiculeTypesOptions"
            :id="`dropdown-type-${option.value}`"
            :key="`dropdown-checkbox-search-${option.value}`"
            v-model:checked="formTypes.types"
            :value="option.value"
            name="dropdown-type"
            class="p-2 pl-3 h-20 text-start flex flex-col justify-between items-start flex-1"
          >
            <YscIconsTypeVehicle
              class="w-12"
              :type="option.value"
            />
            <label
              class="mt-4 font-semibold"
              for="`dropdown-type-${option.value}`"
            >{{ option.text }}</label>
          </YscInputCheckboxCard>
        </div>
        <button
          class="link link-primary text-sm"
          type="button"
          role="button"
          @click.prevent="currentForm = FORM_MAP.TYPES"
        >
          {{ $t('components.app_search.display_all_vehicle_types') }}
        </button>
      </div>

      <AppSearchFilterFormSection :title="$t('components.app_search.filters.vehicle_spec')">
        <YscInputCheckbox
          v-if="!isCrossedResults"
          id="vehicle_height"
          v-model="formMain.vehicle_height"
          name="vehicle_height"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsRoadSignTwoMetersHeight
              aria-hidden="true"
              class="h-5 w-5"
            />

            {{ $t('components.app_search.filters.vehicle_height') }}
          </div>
        </YscInputCheckbox>
        <YscInputCheckbox
          id="less_than_five"
          v-model="formMain.less_than_five"
          name="less_than_five"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsBirthdayCake
              aria-hidden="true"
              class="h-5 w-5"
            />

            {{ $t('components.app_search.filters.less_than_five') }}
          </div>
        </YscInputCheckbox>
        <YscInputCheckbox
          v-if="!isCrossedResults"
          id="disabled_access"
          v-model="formMain.disabled_access"
          name="disabled_access"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsWheelchair
              aria-hidden="true"
              class="h-5 w-5"
            />
            {{ $t('components.app_search.filters.disabled_access') }}
          </div>
        </YscInputCheckbox>
        <YscInputCheckbox
          id="smoking"
          v-model="vehicleSmokingDisallowed"
          name="smoking"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          <div class="flex flex-row gap-3 items-center">
            <YscIconsSmokingNotAllowed
              aria-hidden="true"
              class="h-5 w-5"
            />{{ $t('components.app_search.filters.smoking_not_allowed') }}
          </div>
        </YscInputCheckbox>
      </AppSearchFilterFormSection>

      <AppSearchFilterFormSection
        :title="$t('commons.equipments')"
      >
        <YscInputCheckbox
          v-for="opt in equipementsDisplayedOnMainForm"
          :id="`equipment_main_${opt.value}`"
          :key="opt.value"
          v-model="formEquipments.equipments"
          name="equipement_main"
          is-block
          :value="opt.value"
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          {{ opt.text }}
        </YscInputCheckbox>

        <button
          class="link link-primary text-sm"
          @click.prevent="currentForm = FORM_MAP.EQUIPMENTS"
        >
          {{ $t('components.app_search.display_all_equipments') }}
        </button>
      </AppSearchFilterFormSection>

      <AppSearchFilterFormSection
        :title="$t('components.app_search.travelers_preferences')"
      >
        <YscInputCheckbox
          id="pets"
          v-model="formMain.pets"
          name="pets"
          class="flex-row-reverse justify-between !space-x-0 w-full"
          is-block
        >
          {{ $t('components.app_search.filters.pets') }}
        </YscInputCheckbox>
        <YscInputCheckbox
          v-if="!isCrossedResults"
          id="disabled-access"
          v-model="formMain.disabled_access"
          name="disabled_access"
          class="flex-row-reverse justify-between !space-x-0 w-full"
          is-block
        >
          {{ $t('components.app_search.filters.disabled_access') }}
        </YscInputCheckbox>
      </AppSearchFilterFormSection>

      <div
        v-if="!isCrossedResults"
        class="flex flex-col px-4 py-6 gap-8 border-t border-gray-200"
      >
        <div
          class="flex flex-col gap-4"
        >
          <span class="block text-base font-semibold">
            {{ $t('components.app_search.filters.minimal_review') }}
          </span>
          <YscToggleGroup
            v-model.number="formMain.review_average__gte"
            class="text-sm w-full z-0"
            name="review"
            grow
            :options="reviewAverageOptions"
          />
        </div>
        <div
          class="flex flex-col gap-4"
        >
          <span class="block text-base font-semibold">
            {{ $t('components.app_search.filters.owner') }}
          </span>
          <YscToggleGroup
            v-model.number="formMain.owner_types"
            class="text-sm z-0"
            name="owner"
            grow
            :options="ownerOptions"
          />
          <YscInputCheckbox
            id="is_ambassador"
            v-model="formMain.is_ambassador"
            name="is_ambassador"
            is-block
            class="flex-row-reverse justify-between !space-x-0 w-full"
          >
            {{ $t('components.app_search.filters.is_ambassador') }}
          </YscInputCheckbox>
        </div>
        <div
          class="flex flex-col gap-4"
        >
          <span class="inline text-base font-semibold">
            {{ $t('components.app_search.filters.driver_license') }}
          </span>
          <YscToggleGroup
            v-model.number="formMain.licence_type"
            class="text-sm z-0"
            name="licence"
            grow
            :options="licenseOptions"
          />
        </div>
      </div>
    </YscForm>

    <!-- SECOND SCREEN VEHICLE TYPES -->
    <AppSearchFilterTypes
      v-if="currentForm === FORM_MAP.TYPES"
      :id="FORM_MAP.TYPES"
      v-model="formMain"
      class="flex flex-col space-y-4"
      @submit="onSubmitFilter(formMain)"
    />

    <AppSearchFiltersEquipments
      v-if="currentForm === FORM_MAP.EQUIPMENTS"
      :id="FORM_MAP.EQUIPMENTS"
      v-model="formMain.equipments"
      @submit="onSubmitFilter(formMain)"
    />

    <template #footer>
      <div class="flex items-center justify-between z-[200]">
        <button
          class="btn btn-tertiary"
          @click="onClearFilter"
        >
          {{ $t('components.app_search.filters.remove_filters') }}
        </button>

        <YscButtonSubmit :form="currentFormInfo.id">
          {{ $t('commons.actions.validate') }}
        </YscButtonSubmit>
      </div>
    </template>
  </YscModal>
</template>

<script setup lang="ts">
import { CAMPER_TYPES_INTEGER } from '@yescapa-dev/ysc-api-js/modern'
import type { RefType } from '~/types/commons'

interface Props {
  show?: boolean
}

withDefaults(defineProps<Props>(), {
  show: false,
})
const emit = defineEmits<{
  close: []
}>()

const PRICE_RANGE = { MIN: 0, MAX: 500 }

const { t } = useI18n()

const { filters, isCrossedResults } = storeToRefs(useSearchStore())
const { map: currencyReferentialMap } = useReferentialMap('currency')

const FORM_MAP = {
  MAIN: 'search-form',
  TYPES: 'search-form-types',
  EQUIPMENTS: 'search-form-equipment',
} as const

const currentForm = ref<typeof FORM_MAP[keyof typeof FORM_MAP]>(FORM_MAP.MAIN)

const formMain = ref<SearchPreferenceForm & SearchFormPrice & SearchFormPassengers & SearchFormEquipments & SearchFormTypes>({
  // Prices
  min_price: 0,
  max_price: 0,
  discount_early: false,
  discount_weekly: false,
  // Preferences
  km_unlimited: false,
  international: false,
  vehicle_height: false,
  less_than_five: false,
  automatic_gear: false,
  licence_type: null,
  review_average__gte: null,
  owner_types: null,
  is_ambassador: false,
  pets: false,
  disabled_access: false,
  instant_booking: false,
  smoking: undefined,
  // Passengers
  seatbelts: null,
  beds: null,
  equipments: [],
  types: [],
})

const { formPrices, initFormPrices, coercedMaxPrice } = useSearchFiltersPricesForm(formMain)
const { initFormPreferences, vehicleSmokingDisallowed } = useSearchFiltersPreferencesForm(formMain)
const { initFormPassengers, showAdvancedPassengers } = useSearchFiltersFormPassengers(formMain)

const { formTypes, initFormTypes } = useSearchFiltersFormTypes(formMain)
const { formEquipments, initFormEquipements } = useSearchFiltersFormEquipments(formMain)

const initFilters = () => {
  initFormPreferences()
  initFormPassengers()
  initFormPrices()
  initFormTypes()
  initFormEquipements()
}

const formatTravelersCount = (count: number | null) => {
  if (count === null) return 0
  if (count === 6) {
    return t('components.app_search.filters.6_more')
  }
  return count
}

const currentFormInfo = computed(() => {
  const { TYPES, EQUIPMENTS, MAIN } = FORM_MAP
  const formMap = {
    [TYPES]: {
      title: t('components.app_search.filters.vehicle_type'),
      id: TYPES,
    },
    [EQUIPMENTS]: {
      title: t('commons.equipments'),
      id: EQUIPMENTS,
    },
    [MAIN]: {
      title: t('components.app_search.filters.preferences'),
      id: MAIN,
    },
  }

  return formMap[currentForm.value] || formMap[MAIN]
})

const isMainFormActive = computed(() => currentForm.value === FORM_MAP.MAIN)

const { vehicleOptions } = useOrderedVehicleOptions()
const mainFormVehiculeTypesOptions = computed(() => {
  return vehicleOptions.value.filter(({ value }) => ([CAMPER_TYPES_INTEGER.VAN, CAMPER_TYPES_INTEGER.CAMPERVAN, CAMPER_TYPES_INTEGER.LOWPROFILE] as number[]).includes(value))
})

const { licenseOptions, ownerOptions, reviewAverageOptions } = useSearchFiltersOptions()

const { equipmentsCategories } = useSearchFiltersOptionsEquipments()

const equipementsDisplayedOnMainForm = computed(() => {
  const equipments = Object.values(equipmentsCategories.value).map(({ options }) => options).reduce<{ value: string, text: string | null }[]>((acc, value) => acc.concat(value), [])

  return equipments.filter(({ value }) => ['cooking', 'fridge', 'shower_int', 'wc', 'ac'].includes(value))
})

const {
  public: {
    currency: { default: defaultCurrency },
  },
} = useRuntimeConfig()
// @ts-expect-error this is valid as long as defaultCurrency is a valid currency value
const currency = computed(() => currencyReferentialMap.value[defaultCurrency])

const onSubmitFilter = (form: RefType<typeof formMain> | RefType<typeof formTypes> | RefType<typeof formEquipments>) => {
  for (const key in form) {
    // @ts-expect-error we know those value will be compatible
    filters.value[key] = form[key]
  }

  if (isMainFormActive.value) {
    emit('close')
    return
  }

  currentForm.value = FORM_MAP.MAIN
}

const resetForm = (form: typeof formMain | typeof formTypes | typeof formEquipments | Record<string, null>) => {
  const initialFilters = initialSearchFiltersFactory()

  if (isRef(form)) {
    for (const key of Object.keys(form.value)) {
      // @ts-expect-error We know key is in initialFilter
      form.value[key] = initialFilters[key]
      // @ts-expect-error We know key is in initialFilter
      filters.value[key] = initialFilters[key]
    }

    return
  }

  for (const key of Object.keys(form)) {
    // @ts-expect-error We know key is in initialFilter
    form[key] = initialFilters[key]
    // @ts-expect-error We know key is in initialFilter
    filters.value[key] = initialFilters[key]
  }
}

const onClearFilter = () => {
  switch (currentForm.value) {
    case FORM_MAP.TYPES:
      resetForm(formTypes)
      break
    case FORM_MAP.EQUIPMENTS:
      resetForm(formEquipments)
      break
    default:
      resetForm(formTypes)
      resetForm(formEquipments)
      resetForm(formMain)
      resetForm({ date_from: null, date_to: null, where: null, longitude: null, latitude: null, radius: null })
      break
  }
  onClose()
}

const onClose = () => {
  if (isMainFormActive.value) {
    emit('close')
    return
  }
  currentForm.value = FORM_MAP.MAIN
}

initFilters()
</script>
